// The pill above the hand: a hint, or a refusal (kit-sheet `.hint`,
// screen-mock `#pill`).
//
// **Full sentences, never ellipsized** (PLAN-UI §28.3). There is deliberately
// no `text-overflow`, no `white-space: nowrap` and no line clamp here: every
// refusal string in the tables is a whole reason, and half a reason is the
// silent no-op the refusal machinery was built to replace. It wraps instead.
//
// **It is the one line on the screen that has to be read** (designer, U7
// review). As first drawn it was 13px in a thin dark chip and read as chrome —
// a refusal is an *answer to something the player just tried*, so it is sized
// and lit like one: body face at 16/700, the kit's hard drop under it, and a
// bloom so it separates from whatever square it is floating over. Loud without
// being modal is the whole brief; it still takes no pointer and still goes
// away on its own.
//
// `bad` is the refusal variant — rust, which is the only place in ordinary
// chrome that colour appears, and here it glows.

import { keyframes, styled } from "@mui/material/styles";

import { REDUCED_MOTION, STILL } from "./motion";

/**
 * The entrance. **Replayed on every announcement**, which is a fact about the
 * caller and not about this file: a CSS animation runs once per mount, so
 * `RefusalPill` keys this element on `pill.spoke` and React remounts it. That
 * is what makes the same sentence refused twice pop twice.
 */
const pop = keyframes({
  from: { opacity: 0, transform: "scale(.88) translateY(6px)" },
  to: { opacity: 1, transform: "none" }
});

export const HintPill = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "bad"
})<{ bad?: boolean }>(({ theme, bad }) => {
  const c = theme.kit.color;
  return {
    display: "inline-block",
    maxWidth: "70vw",
    padding: "10px 22px",
    textAlign: "center",
    fontFamily: theme.kit.font.body,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.4,
    overflowWrap: "break-word",
    borderRadius: theme.kit.radius.pill,
    border: `2.5px solid ${bad ? c.rustEdge : c.line}`,
    // Near-black glass so the sentence reads over any square under it — a
    // little more opaque than it was, because the sentence outranks the board
    // it is covering for the two seconds it is up.
    background: bad ? c.rust : `color-mix(in srgb, ${c.shadow} 92%, transparent)`,
    color: bad ? c.onRust : c.cream,
    // The kit's hard drop (`Btn`, `GameCard`), plus a bloom: the drop seats it
    // on the board and the bloom is what makes a refusal impossible to skim
    // past. Rust for a refusal, the border's own gunmetal for a hint, so the
    // two are told apart at a glance and only one of them shouts.
    boxShadow: `0 4px 0 ${c.shadow}, 0 0 22px color-mix(in srgb, ${
      bad ? c.rust : c.line
    } 55%, transparent)`,
    animation: `${pop} ${theme.kit.motion.duration.draw}ms ${theme.kit.motion.easeBounce} both`,
    [REDUCED_MOTION]: STILL
  };
});
